import React from 'react';
import './PriceChart.scss';

interface PricePoint {
  price: number;
  date: string;
}

interface PriceChartProps {
  history: PricePoint[];
  currentPrice?: number;
}

const PriceChart: React.FC<PriceChartProps> = ({ history }) => {
  if (history.length < 1) {
    return (
      <div className="price-chart price-chart--empty">
        <span className="price-chart-label">Price History</span>
        <span className="price-chart-no-data">No trade data yet</span>
      </div>
    );
  }

  // With a single point, duplicate it so the SVG polyline renders as a flat line
  const history2 = history.length === 1 ? [history[0]!, history[0]!] : history;

  const W = 260;
  const H = 60;
  const PAD = 4;

  const prices = history2.map(p => p.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;

  const toX = (i: number) => PAD + (i / (history2.length - 1)) * (W - PAD * 2);
  const toY = (price: number) => PAD + (1 - (price - min) / range) * (H - PAD * 2);

  const polyline = history2.map((p, i) => `${toX(i)},${toY(p.price)}`).join(' ');

  // Fill path — close below the line
  const fillPath =
    `M ${toX(0)},${toY(history2[0]!.price)} ` +
    history2.slice(1).map((p, i) => `L ${toX(i + 1)},${toY(p.price)}`).join(' ') +
    ` L ${toX(history2.length - 1)},${H} L ${toX(0)},${H} Z`;

  const last = history[history.length - 1]!;
  const first = history[0]!;
  const isUp = last.price >= first.price;
  const pctChange = first.price > 0
    ? (((last.price - first.price) / first.price) * 100).toFixed(1)
    : null;

  const colorClass = isUp ? 'up' : 'down';
  const strokeColor = isUp ? 'var(--success-color)' : 'var(--danger-color)';
  const fillColor = isUp ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)';

  const formatPrice = (p: number) => {
    if (p >= 1000) return `${(p / 1000).toFixed(1)}K`;
    if (p >= 1) return p.toFixed(2);
    return p.toFixed(8).replace(/\.?0+$/, '');
  };

  return (
    <div className={`price-chart price-chart--${colorClass}`}>
      <div className="price-chart-header">
        <span className="price-chart-label">Price History</span>
        {pctChange !== null && (
          <span className="price-chart-change">
            {isUp ? '▲' : '▼'} {Math.abs(parseFloat(pctChange))}%
          </span>
        )}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="price-chart-svg"
        preserveAspectRatio="none"
      >
        {/* Gradient fill under line */}
        <path d={fillPath} fill={fillColor} />
        {/* Sparkline */}
        <polyline
          points={polyline}
          fill="none"
          stroke={strokeColor}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* End dot */}
        <circle
          cx={toX(history2.length - 1)}
          cy={toY(last.price)}
          r="3"
          fill={strokeColor}
        />
      </svg>

      <div className="price-chart-footer">
        <span className="price-chart-start">{formatPrice(first.price)}</span>
        <span className="price-chart-trades">{history.length} trade{history.length !== 1 ? 's' : ''}</span>
        <span className="price-chart-end">{formatPrice(last.price)}</span>
      </div>
    </div>
  );
};

export default PriceChart;
